import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Folder20Filled,
    DocumentBulletList20Filled,
    Warning20Filled,
    Sparkle20Filled,
    CheckmarkCircle20Filled,
} from '@fluentui/react-icons';
import { useAccuracyAskLoaderSimulation } from '../../../hooks/useAccuracyAskLoaderSimulation';
import styles from '../css/index.module.css';

const STEP_ICONS = {
    folder: Folder20Filled,
    checklist: DocumentBulletList20Filled,
    warning: Warning20Filled,
    sparkles: Sparkle20Filled,
};

const STACK_BEHIND = [
    { scale: 0.93, translateX: 0, opacity: 1 },
    { scale: 1.02, translateX: -18, opacity: 0.45 },
    { scale: 1.05, translateX: 18, opacity: 0.35 },
];

const CARD_TRANSITION = { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] };

function StepIcon({ iconKey, pulse }) {
    const Icon = STEP_ICONS[iconKey] || Folder20Filled;
    if (!pulse) {
        return <Icon className={styles.aiWardenAskLoaderCardIcon} aria-hidden="true" />;
    }
    return (
        <motion.span
            className={styles.aiWardenAskLoaderCardIconWrap}
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            aria-hidden="true">
            <Icon className={styles.aiWardenAskLoaderCardIcon} />
        </motion.span>
    );
}

function AnimatedEllipsis() {
    return (
        <span className={styles.aiWardenAskLoaderEllipsis} aria-hidden="true">
            <span className={styles.aiWardenAskLoaderEllipsisDot} />
            <span className={styles.aiWardenAskLoaderEllipsisDot} />
            <span className={styles.aiWardenAskLoaderEllipsisDot} />
        </span>
    );
}

/**
 * Option 8 — centered card stack with bottom timeline (scope-aware steps).
 */
const AccuracyAskLoader = ({ active, scope = {}, scopeLabel = '' }) => {
    const sim = useAccuracyAskLoaderSimulation({ active, scope });

    if (!active) return null;

    const { stepsWithStatus, stageIndex, currentStage, activeMessage, stepPlan, totalSteps } = sim;
    const stackSteps = stepPlan.slice(stageIndex, stageIndex + 3);

    return (
        <section
            className={styles.aiWardenAskLoader}
            aria-busy="true"
            aria-label="Building your answer"
            data-testid="accuracy-ask-loader">
            <div className={styles.aiWardenAskLoaderStackWrap}>
                <div className={styles.aiWardenAskLoaderStack}>
                    <AnimatePresence mode="popLayout">
                        {stackSteps
                            .slice()
                            .reverse()
                            .map((step, reverseIndex) => {
                                const depth = stackSteps.length - 1 - reverseIndex;
                                const isTop = depth === 0;
                                const stackStyle = STACK_BEHIND[depth] ?? STACK_BEHIND[2];

                                if (isTop) {
                                    return (
                                        <motion.article
                                            key={step.id}
                                            className={`${styles.aiWardenAskLoaderCard} ${styles.aiWardenAskLoaderCardActive}`}
                                            initial={{ opacity: 0, y: 12 }}
                                            animate={{ opacity: 1, y: 0, scale: STACK_BEHIND[0].scale }}
                                            exit={{ opacity: 0, y: -8 }}
                                            transition={CARD_TRANSITION}
                                            style={{ zIndex: 3 }}>
                                            <span className={styles.aiWardenAskLoaderCardShimmer} aria-hidden="true" />
                                            <StepIcon iconKey={step.icon} pulse />
                                            <h3 className={styles.aiWardenAskLoaderCardTitle}>{step.label}</h3>
                                            <p className={styles.aiWardenAskLoaderCardDesc}>
                                                {activeMessage}
                                                <AnimatedEllipsis />
                                            </p>
                                        </motion.article>
                                    );
                                }

                                return (
                                    <div
                                        key={`${step.id}-depth-${depth}`}
                                        className={styles.aiWardenAskLoaderCard}
                                        style={{
                                            zIndex: 3 - depth,
                                            transform: `translateX(${stackStyle.translateX}px) scale(${stackStyle.scale})`,
                                            opacity: stackStyle.opacity,
                                        }}
                                        aria-hidden="true">
                                        <StepIcon iconKey={step.icon} pulse={false} />
                                        <span className={styles.aiWardenAskLoaderCardTitleMuted}>{step.label}</span>
                                    </div>
                                );
                            })}
                    </AnimatePresence>
                </div>
            </div>

            <ol className={styles.aiWardenAskLoaderTimeline} aria-label="Evidence retrieval progress">
                {stepsWithStatus.map(step => {
                    const isDone = step.status === 'done';
                    const isActive = step.status === 'active';
                    const itemClass = [
                        styles.aiWardenAskLoaderTimelineItem,
                        isDone ? styles.aiWardenAskLoaderTimelineItemDone : '',
                        isActive ? styles.aiWardenAskLoaderTimelineItemActive : '',
                        step.status === 'pending' ? styles.aiWardenAskLoaderTimelineItemPending : '',
                    ]
                        .filter(Boolean)
                        .join(' ');

                    return (
                        <li key={step.id} className={itemClass}>
                            <span className={styles.aiWardenAskLoaderTimelineMarker} aria-hidden="true">
                                {isDone ? (
                                    <CheckmarkCircle20Filled className={styles.aiWardenAskLoaderTimelineCheck} />
                                ) : (
                                    <span className={styles.aiWardenAskLoaderTimelineDot} />
                                )}
                            </span>
                            <span className={styles.aiWardenAskLoaderTimelineLabel}>{step.label}</span>
                        </li>
                    );
                })}
            </ol>

            {scopeLabel ? <p className={styles.aiWardenAskLoaderScope}>Scope: {scopeLabel}</p> : null}

            <span className={styles.aiWardenAskLoaderSrOnly}>
                Step {stageIndex + 1} of {totalSteps}: {currentStage?.label}
            </span>
        </section>
    );
};

export default AccuracyAskLoader;
